/**
 * 将浏览器原生 location.search 中的参数合并到 hash query 中。
 *
 * 背景：Vue Router hash 模式下，路由的 query 存储在 # 之后，
 * 而浏览器原生的 location.search（# 之前的部分）对 Vue Router 不可见。
 * 此函数需要在 createRouter 调用之前执行，确保路由初始化时能读取到完整的参数。
 *
 * 合并规则：
 * - hash query 中的参数优先级高于 location.search（hash 覆盖 search）
 * - 多值参数（同名 key 出现多次）完整保留
 * - 处理完毕后清除 location.search，避免重复处理和 URL 冗余
 *
 * 示例：
 *   URL: http://example.com/app?utm_source=email&tab=2#/home?tab=1
 *   合并后: http://example.com/app#/home?utm_source=email&tab=1
 *   （tab 以 hash 中的值 1 为准）
 */
export function syncLocationSearchToHashQuery(): void {
    if (!window.location.search) {
        return;
    }

    const browserParams = new URLSearchParams(window.location.search);

    const rawHash = window.location.hash;
    const hashWithoutPound = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;

    const questionMarkIndex = hashWithoutPound.indexOf('?');
    const hashPath = questionMarkIndex >= 0
        ? hashWithoutPound.slice(0, questionMarkIndex)
        : hashWithoutPound;
    const hashQueryString = questionMarkIndex >= 0
        ? hashWithoutPound.slice(questionMarkIndex + 1)
        : '';

    const hashParams = new URLSearchParams(hashQueryString);

    // 以 browser params 为基础，hash params 中存在的 key 整体覆盖（含多值）
    const merged = new URLSearchParams(browserParams.toString());
    const hashKeys = new Set<string>();
    hashParams.forEach((_, key) => hashKeys.add(key));
    hashKeys.forEach(key => {
        merged.delete(key);
        hashParams.getAll(key).forEach(value => merged.append(key, value));
    });

    const mergedString = merged.toString();
    const newHashContent = hashPath + (mergedString ? `?${mergedString}` : '');

    history.replaceState(
        null,
        '',
        window.location.pathname + '#' + newHashContent
    );
}
